import {useCallback, useMemo, useState} from 'react';

import {ConfigDB} from '~/modules/api/configDB.types';
import {useUpdateConfigMutation} from '~/store/rtkQuery';

type DiscountTypeRow = {
  discountType: string;
  audience: string;
  title: string;
  description: string;
};

export const ManagePurchaselyDiscountTypes = ({
  discountTypes,
  onConfigUpdated,
}: {
  discountTypes: ConfigDB.Sysadmin.Purchasely.DiscountTypes;
  onConfigUpdated: () => void;
}) => {
  const [updateConfig, {error}] = useUpdateConfigMutation();

  const [addNew, setAddNew] = useState(false);
  const [editDiscountType, setEditDiscountType] =
    useState<DiscountTypeRow | null>(null);

  const [discountType, setDiscountType] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience] = useState('parents'); // todo - enable setting audience for new discount types

  const purchaselyDiscountTypes = useMemo((): DiscountTypeRow[] => {
    if (!discountTypes) {
      return [];
    }
    return Object.entries(discountTypes).map(([discountType, values]) => {
      return {
        discountType,
        audience: values.audience,
        title: values.title,
        description: values.description,
      };
    });
  }, [discountTypes]);

  const resetForm = useCallback(() => {
    setDiscountType('');
    setTitle('');
    setDescription('');
  }, []);

  const onEditItem = useCallback((item: DiscountTypeRow | null) => {
    setEditDiscountType(item);
    setTitle(item?.title ?? '');
    setDescription(item?.description ?? '');
  }, []);

  const onCreateDiscountType = useCallback(async () => {
    const response = await updateConfig({
      config: 'purchasely/discountTypes',
      action: 'create',
      data: {
        audience,
        discountType,
        title,
        description,
      },
    });
    if (!('error' in response)) {
      onConfigUpdated();
      resetForm();
      setAddNew(false);
    }
  }, [
    updateConfig,
    onConfigUpdated,
    resetForm,
    discountType,
    audience,
    title,
    description,
  ]);

  const onEditDiscountCode = useCallback(async () => {
    if (!editDiscountType) {
      return null;
    }
    const response = await updateConfig({
      config: 'purchasely/discountTypes',
      action: 'update',
      data: {
        discountType: editDiscountType.discountType,
        audience: editDiscountType.audience,
        title,
        description,
      },
    });
    if (!('error' in response)) {
      onConfigUpdated();
      resetForm();
      setEditDiscountType(null);
    }
  }, [
    updateConfig,
    onConfigUpdated,
    resetForm,
    editDiscountType,
    title,
    description,
  ]);

  return (
    <>
      <div>
        <table className="table">
          <thead>
            <tr>
              <th>Discount Type</th>
              <th>Audience</th>
              <th>Title</th>
              <th>Description</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {purchaselyDiscountTypes.map(discountType => {
              const isEditing =
                editDiscountType?.discountType === discountType.discountType;
              return (
                <tr key={discountType.discountType}>
                  <th>{discountType.discountType}</th>
                  <td>{discountType.audience}</td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={title}
                        className="input input-bordered w-full"
                        onChange={e => setTitle(e.target.value)}
                      />
                    ) : (
                      discountType.title
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <textarea
                        value={description}
                        className="textarea textarea-bordered w-full"
                        onChange={e => setDescription(e.target.value)}
                      />
                    ) : (
                      discountType.description
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <>
                        <button
                          className="btn btn-sm mr-2 my-2"
                          onClick={() => onEditItem(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm btn-primary my-2"
                          onClick={onEditDiscountCode}>
                          Save
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn"
                        onClick={() => onEditItem(discountType)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>
                <button
                  className="btn btn-primary"
                  onClick={() => setAddNew(true)}>
                  Add new discount type
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {addNew && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <div className="text-2xl">Create Discount Type</div>

            <div className="py-4">
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Discount Type</span>
                  <input
                    type="text"
                    value={discountType}
                    className="input input-bordered"
                    onChange={e => setDiscountType(e.target.value)}
                  />
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Audience</span>
                  <input
                    type="text"
                    value="parents"
                    disabled
                    className="input input-bordered"
                  />
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Title</span>
                  <input
                    type="text"
                    value={title}
                    className="input input-bordered"
                    onChange={e => setTitle(e.target.value)}
                  />
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Description</span>
                  <input
                    type="text"
                    value={description}
                    className="input input-bordered"
                    onChange={e => setDescription(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {!!error && (
              <div role="alert" className="alert alert-error">
                <span>{error.message}</span>
              </div>
            )}

            <div className="modal-action">
              <button
                className="btn"
                onClick={() => {
                  resetForm();
                  setAddNew(false);
                }}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={onCreateDiscountType}>
                Save
              </button>
            </div>
          </div>
        </dialog>
      )}
    </>
  );
};
